<template>
  <div>
    <v-data-table
      :items="tableItems"
      v-bind="$attrs"
      :class="fixed ? 'fixed-layout' : undefined"
      fixed-header
      :style="{
        'max-height': stickyHeader,
      }"
      :headers="tableFields"
      :item-value="primaryKey"
      :items-per-page="-1"
    >
      <template v-slot="{ internalItems }">
        <!--        HEADER-->
        <thead>
          <tr>
            <template v-for="field in tableFields" :key="`header${field.key}}`">
              <th
                class="v-data-table__td v-data-table-column--align-center v-data-table__th"
                :style="{
                  width:
                    field.key === '_handle'
                      ? '50px'
                      : field.width
                        ? field.width
                        : 'auto',
                }"
              >
                <div class="v-data-table-header__content justify-center">
                  <slot :name="`head(${field.key})`" v-bind="field">
                    <span>{{ field.title }}</span>
                  </slot>
                </div>
              </th>
            </template>
          </tr>
        </thead>
        <!--        BODY-->
        <draggable
          :list="tableItems"
          tag="tbody"
          :item-key="primaryKey"
          handle=".handle"
          ghost-class="drag-ghost"
          draggable=".draggable"
        >
          <template v-slot:item="{ element, index }">
            <v-data-table-row
              :item="internalItems[index]"
              :cell-props="mergeTdClass"
              v-bind="mergeTrClass(element)"
            >
              <template
                v-for="field in tableFields"
                :key="field.key"
                v-slot:[`item.${field.key}`]
              >
                <slot
                  :name="`cell(${field.key})`"
                  :value="
                    formatterMap[field.key]
                      ? formatterMap[field.key].formatter(
                          element[field.key],
                          field.key,
                          element
                        )
                      : element[field.key]
                  "
                  :item="element"
                >
                  <template v-if="field.key === '_handle'">
                    <v-icon size="small" icon="$mdiMenu"></v-icon>
                  </template>
                  <template v-else-if="formatterMap[field.key]">
                    {{
                      formatterMap[field.key].formatter(
                        element[field.key],
                        field.key,
                        element
                      )
                    }}
                  </template>
                  <template v-else>{{ element[field.key] }}</template>
                </slot>
              </template>
            </v-data-table-row>
          </template>
        </draggable>
      </template>
      <!--      DraggableテーブルのPagingは不要-->
      <template v-slot:bottom></template>
    </v-data-table>
  </div>
</template>

<script lang="ts">
export type AppDraggableTableHeader<T> = {
  readonly key: string;
  readonly title: string;
  readonly align?: "center" | "end" | "start";
  readonly width?: string | number;
  formatter?: (value: any, key: string, item: T) => any;
};

import { defineComponent } from "vue";
import type { PropType } from "vue";
import draggable from "vuedraggable";
import type { TableItem } from "@/components/AppTable.vue";

/**
 *  v-data-tableとvue.draggable.nextでAppDraggableTableを実装
 *  @see https://github.com/SortableJS/vue.draggable.next
 *
 */
export default defineComponent({
  name: "AppDraggableTable",
  inheritAttrs: false,
  components: { draggable },
  data() {
    return {
      /** 選択済みitems */
      tableSelected: this.modelValue,
      /** 一覧のソート項目の保存用 */
      currentSortBy: this.tableSortBy ? [this.tableSortBy] : [],
    };
  },
  props: {
    // カラム定義
    fields: {
      type: Array as PropType<Array<AppDraggableTableHeader<any>>>,
      required: true,
    },
    // データ配列
    items: {
      type: Array as PropType<Array<any>>,
      required: true,
    },
    /**
     * 主キー相当のプロパティ名.
     * Draggableの管理などで使用されているので必須
     */
    primaryKey: {
      type: String as PropType<string>,
      required: true,
    },
    /** テーブル高さ */
    stickyHeader: {
      type: String as PropType<string>,
      default: "300px",
    },
    /** tdクラス設定のCallBack */
    tdClass: {
      type: Function as PropType<
        (field: AppDraggableTableHeader<unknown>) => string[] | undefined
      >,
      required: false,
    },
    /** trクラス設定のCallBack */
    trClass: {
      type: Function as PropType<(item: any) => string[] | undefined>,
      required: false,
    },
    /** table-layout:fixを適用か */
    fixed: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
  },
  mounted() {},
  computed: {
    tableFields(): Pick<
      AppDraggableTableHeader<TableItem>,
      "key" | "title" | "align" | "width"
    >[] {
      return this.fields.map((v) => {
        const { key } = v;
        return {
          key: key,
          title: v.title,
          align: v.align ?? "start",
          width: v.width,
        };
      });
    },
    /** table表示のitems */
    tableItems(): TableItem[] {
      return this.items;
    },
    formatterMap(): Record<string, { key: string; formatter: Function }> {
      return (
        this.fields.flatMap((e) => {
          if (typeof e === "object") {
            if (typeof e.formatter === "function") {
              return [e];
            }
            return [];
          }
        }) as { key: string; formatter: Function }[]
      ).toMap((e) => e.key);
    },
  },
  methods: {
    /** td Classの生成
     *  field nameは[_handle]の項目に「handle」クラスを追加
     * 「handle」クラスがある列はDraggableアイコンが表示される
     */
    mergeTdClass({
      column: field,
    }: {
      column: AppDraggableTableHeader<unknown>;
    }): { class: string } | undefined {
      const classes = [];
      if (field.key === "_handle") {
        classes.push("handle");
      }
      if (this.tdClass) {
        const customClasses = this.tdClass(field);
        if (customClasses && customClasses.length > 0) {
          classes.push(...customClasses);
        }
      }
      if (classes.length > 0) {
        return { class: classes.join(" ") };
      }
      return undefined;
    },
    /**
     *  tr Classの生成
     * 「draggable」がある行はドラッグ可能
     * 「clickable」がある行はクリック可能
     * 「gray-out」がある行は灰色
     */
    mergeTrClass(
      item: TableItem
    ): Record<string, string | Function> | undefined {
      const classes = [];
      if (this.trClass) {
        const customClasses = this.trClass(item);
        if (customClasses && customClasses.length > 0) {
          classes.push(...customClasses);
        }
      } else {
        // デフォルトはclickable
        classes.push("clickable");
      }
      if (classes.length > 0) {
        // クリックできる行のみ、イベントを追加
        if (classes.includes("clickable")) {
          return {
            class: classes.filter((v) => v !== "clickable").join(" "),
            onClick: () => {
              this.$emit("click:row", item);
            },
          };
        } else {
          return {
            class: classes.join(" "),
          };
        }
      }
      return undefined;
    },
  },
});
</script>
<style lang="scss" scoped>
tr:not(.draggable) {
  td.handle > i {
    display: none;
  }
}

.drag-ghost {
  opacity: 0;
}
</style>
